import { useEffect, useRef, useState } from 'react';
import {
  LayoutGrid,
  SquareStack,
  ListChecks,
  Cable,
  FolderPlus,
  FileText,
  PanelsTopLeft,
} from 'lucide-react';
import type { SheetKind } from '../types';

/**
 * Shared modal dialogs used to add / rename / delete sheets. They live in
 * their own module so both the (now-removed) tab bar and the project tree
 * can share the same UI without duplicating overlay behaviour.
 */

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};

const cardStyle: React.CSSProperties = {
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border-strong)',
  borderTop: '2px solid var(--accent)',
  borderRadius: 'var(--radius)',
  padding: 18,
  minWidth: 380,
  boxShadow: 'var(--shadow-lg)',
};

interface NameModalProps {
  title: string;
  description?: string;
  defaultValue: string;
  okLabel?: string;
  onCancel: () => void;
  onSubmit: (value: string) => void;
}

export function NameModal({
  title,
  description,
  defaultValue,
  okLabel = 'Add',
  onCancel,
  onSubmit,
}: NameModalProps) {
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);
  const submit = () => {
    const v = value.trim();
    if (!v) return;
    onSubmit(v);
  };
  return (
    <div onMouseDown={onCancel} style={overlayStyle}>
      <div onMouseDown={(e) => e.stopPropagation()} style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{title}</div>
        {description && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-muted)',
              marginBottom: 12,
              whiteSpace: 'pre-wrap',
              lineHeight: 1.5,
            }}
          >
            {description}
          </div>
        )}
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onCancel();
            }
          }}
          style={{
            width: '100%',
            background: 'var(--input-bg, var(--panel))',
            color: 'var(--text)',
            border: '1px solid var(--panel-border)',
            borderRadius: 4,
            padding: '6px 8px',
            fontSize: 13,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button onClick={onCancel} style={cancelBtn}>
            Cancel
          </button>
          <button onClick={submit} style={primaryBtn}>
            {okLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

interface PickKindModalProps {
  onCancel: () => void;
  onPick: (kind: SheetKind | 'subsystem') => void;
}

export function PickKindModal({ onCancel, onPick }: PickKindModalProps) {
  const opts: {
    kind: SheetKind | 'subsystem';
    label: string;
    desc: string;
    Icon: typeof LayoutGrid;
  }[] = [
    {
      kind: 'subsystem',
      label: 'Subsystem',
      desc: 'Requirements + Block + Schematic trio (e.g. "Robot")',
      Icon: FolderPlus,
    },
    { kind: 'block', label: 'Block diagram', desc: 'High-level subsystem view', Icon: LayoutGrid },
    {
      kind: 'schematic',
      label: 'Schematic',
      desc: 'Pin-level wiring sheet',
      Icon: SquareStack,
    },
    {
      kind: 'mechanical',
      label: 'Mechanical layout',
      desc: 'Free cabinet / panel placement sheet',
      Icon: PanelsTopLeft,
    },
    {
      kind: 'requirements',
      label: 'Requirements',
      desc: 'Requirement list sheet',
      Icon: ListChecks,
    },
    {
      kind: 'interconnect',
      label: 'Harnesses',
      desc: 'Cable harnesses — within & between subsystems',
      Icon: Cable,
    },
    {
      kind: 'document',
      label: 'Document',
      desc: 'Free-form text — installation, maintenance, manual',
      Icon: FileText,
    },
  ];
  return (
    <div onMouseDown={onCancel} style={overlayStyle}>
      <div onMouseDown={(e) => e.stopPropagation()} style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Add to project</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {opts.map(({ kind, label, desc, Icon }) => (
            <button
              key={kind}
              onClick={() => onPick(kind)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: 'transparent',
                color: 'var(--text)',
                border: '1px solid var(--panel-border)',
                borderRadius: 4,
                padding: '8px 10px',
                fontSize: 12,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <Icon size={14} />
              <div>
                <div style={{ fontWeight: 600 }}>{label}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{desc}</div>
              </div>
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button onClick={onCancel} style={cancelBtn}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

interface ConfirmModalProps {
  title: string;
  message: string;
  destructive?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmModal({
  title,
  message,
  destructive = true,
  onCancel,
  onConfirm,
}: ConfirmModalProps) {
  return (
    <div onMouseDown={onCancel} style={overlayStyle}>
      <div onMouseDown={(e) => e.stopPropagation()} style={{ ...cardStyle, minWidth: 360 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
          {message}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onCancel} style={cancelBtn}>
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              ...primaryBtn,
              background: destructive ? 'var(--danger)' : 'var(--accent)',
            }}
          >
            {destructive ? 'Delete' : 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
}

const cancelBtn: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--panel-border)',
  borderRadius: 4,
  padding: '5px 12px',
  fontSize: 12,
  cursor: 'pointer',
};

const primaryBtn: React.CSSProperties = {
  background: 'var(--accent)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  padding: '5px 14px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
};

export type AddDialog =
  | { mode: 'subsystem'; defaultName: string }
  | { mode: 'sheet'; kind: SheetKind; defaultName: string }
  | { mode: 'pickKind' }
  | null;
